import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import LegalShell from "@/components/LegalShell";
import SEO from "@/components/SEO";
import { API_BASE } from "@/lib/api";

/**
 * Data Deletion Request page.
 *
 * Required by Meta App Review (Facebook + Instagram). Anyone — logged in or
 * not — can submit a deletion request. The form posts to the public
 * /api/legal/data-deletion-request endpoint; submissions land in the Admin
 * dashboard for the platform owner to process.
 */
export default function DataDeletionPage() {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmationId, setConfirmationId] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/legal/data-deletion-request`,
        { email: trimmed, note: note.trim() || null },
      );
      setSubmitted(true);
      setConfirmationId(res?.data?.request_id || null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit request. Try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Data Deletion Request | Postmoro"
        description="Request deletion of your Postmoro account and associated data. Submit a request or follow the self-service steps to remove your information."
        path="/data-deletion"
      />
      <LegalShell
      title="Data Deletion"
      subtitle="Delete your Postmoro account and request removal of your data. Required for Meta App Review compliance."
      lastUpdated="August 2026"
      testidPrefix="data-deletion"
    >
      <p>
        <strong>Postmoro LLC</strong> provides the following options for deleting your
        Postmoro account, connected-platform data, and other information associated with
        your account.
      </p>
      <h2>How to Delete Your Account</h2>
      <p>
        If you have an active Postmoro account, the fastest way to delete it is:
      </p>
      <ol className="list-decimal pl-5 my-3 space-y-1">
        <li>Sign in to your Postmoro account.</li>
        <li>Go to <strong>Settings → Connections</strong> and disconnect any linked social accounts.</li>
        <li>Email <a href="mailto:support@postmoro.com">support@postmoro.com</a> from the email address on file requesting account deletion, OR use the form below.</li>
      </ol>
      <p>
        We aim to process deletion requests within <strong>30 days</strong> unless a longer
        period is required or permitted by law. When we process your request we will remove
        or anonymize the following data:
      </p>
      <ul>
        <li>Your account and authentication information.</li>
        <li>Your business profile data.</li>
        <li>Your business contact details (phone, email, website, address, hours, services, and similar fields you entered).</li>
        <li>Your Brand Learning examples and saved Brand Style Profile.</li>
        <li>Your generated content (captions, hashtags, AI images, drafts, and scheduled posts).</li>
        <li>Your Media Library images and their metadata.</li>
        <li>Your Competitor Analysis workspace records (competitor entries, AI analyses, opportunities, and saved reports) if you use that feature.</li>
        <li>Your Email Marketing workspace records (contact lists, imported contacts, campaign drafts, and Autopilot settings) if you use that feature.</li>
        <li>Your connected social account tokens and platform identifiers.</li>
        <li>Your support tickets, unless retained for legal, security, or compliance reasons.</li>
      </ul>
      <p>
        Some records may be retained as required by law or for legitimate operational reasons.
        For example, <strong>billing receipts may be retained as legally required</strong> for
        tax, accounting, and financial-reporting purposes.
      </p>

      <h2>Postmoro Does Not Sell Your Data</h2>
      <p>
        Postmoro does <strong>not</strong> sell your data. Deletion requests are used to
        remove your account data from Postmoro systems, subject to limited retention required
        for billing, security, legal, compliance, or dispute-resolution purposes.
      </p>

      <h2>How to Disconnect Social Accounts</h2>
      <p>
        You can disconnect any connected social account from inside Postmoro at any time:
      </p>
      <ul>
        <li><strong>Facebook Page</strong> — Settings → Connections → Facebook Page → Disconnect.</li>
        <li><strong>Instagram</strong> — Settings → Connections → Instagram → Disconnect.</li>
        <li><strong>LinkedIn</strong> — Settings → Connections → LinkedIn → Disconnect.</li>
        <li><strong>X / Twitter</strong> — Settings → Connections → X / Twitter → Disconnect.</li>
      </ul>
      <p>
        Disconnecting immediately removes the stored OAuth tokens and stops any further
        publishing to that platform on your behalf.
      </p>

      <h2>Meta App Review Reviewers</h2>
      <p>
        If you are reviewing this application on behalf of Meta, this is the official Data
        Deletion endpoint for Postmoro:
      </p>
      <ul>
        <li><strong>Public Data Deletion URL:</strong> <a href="/data-deletion">/data-deletion</a> (this page).</li>
        <li><strong>Contact email:</strong> <a href="mailto:support@postmoro.com">support@postmoro.com</a>.</li>
        <li><strong>Privacy Policy:</strong> <Link to="/privacy">/privacy</Link>.</li>
        <li><strong>Terms of Service:</strong> <Link to="/terms">/terms</Link>.</li>
      </ul>
      <p>
        Submitting the form below files an official deletion request. Postmoro will process
        the request and respond to the email provided.
      </p>

      <h2>Request Data Deletion</h2>
      {submitted ? (
        <div
          className="rounded-lg border border-[color:rgba(27,168,159,0.4)] bg-[color:rgba(27,168,159,0.06)] p-4 flex items-start gap-3"
          data-testid="data-deletion-success"
        >
          <CheckCircle2 className="h-5 w-5 text-[color:var(--success,#1ba89f)] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[color:var(--text-1)] font-semibold">Your deletion request has been received.</div>
            <p className="text-sm text-[color:var(--text-2)] mt-1">
              We&apos;ll process it as required. You&apos;ll get a confirmation email at the address you submitted.
            </p>
            {confirmationId ? (
              <p className="text-xs text-[color:var(--text-3)] mt-2 font-mono">
                Reference: {confirmationId}
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-2 space-y-3" data-testid="data-deletion-form">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
              Email
            </label>
            <div className="relative mt-1">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-3)]" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="pl-9 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
                data-testid="data-deletion-email"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">
              Optional note
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything you'd like us to know about your deletion request (optional)."
              rows={3}
              className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]"
              data-testid="data-deletion-note"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold disabled:opacity-60"
            data-testid="data-deletion-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Submit deletion request
          </Button>
        </form>
      )}
    </LegalShell>
    </>
  );
}
